<template lang="pug">
.other-office-connection-list-tab
  .mb-3
  OtherOfficeConnectionTable(v-model="_items" :fields="fields" :totalRows.sync="_rows" :currentPage.sync="currentPage" :perPage="perPage"
    :isLoading="isLoading" :filter="filter" @onDetail="onDetail")
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="canceled-waiter-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・事業所名
          div ・ユーザー名
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
    // ページネーション
    b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)
</template>

<script lang="ts">
import { Component, Watch, Prop, Vue } from "vue-property-decorator";

import Setting from "../../../../public/setting.json";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import OtherOfficeConnectionTable, {
  OtherOfficeConnectionTableField,
  OtherOfficeConnectionTableItem,
  FilterSetting
} from "@/pc/containers/sendOtherOfficeMessages/OtherOfficeConnectionTable.vue";

/**
 * ### 取下げ待機者一覧画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    OtherOfficeConnectionTable
  }
})
export default class OtherOfficeConnectionListTab extends Vue {
  /** 読込み中か否か */
  @Prop()
  isLoading!: boolean;
  /** 行情報 */
  @Prop()
  items!: Array<OtherOfficeConnectionTableItem>;
  /** ページングの全体行数 */
  @Prop()
  rows!: number;

  /** 列名 */
  fields: Array<OtherOfficeConnectionTableField> = [];
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.otherOffice.message.connectionListTab.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.otherOffice.message.connectionListTab.pageOptions;

  get _items(): Array<OtherOfficeConnectionTableItem> {
    return this.items;
  }

  get _rows(): number {
    return this.rows;
  }

  set _rows(newValue: number) {
    this.$emit("update:rows", newValue);
    return;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `other-office-message-connection-list-tab-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<OtherOfficeConnectionTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.otherOffice.message.connectionListTab.perPage);

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<OtherOfficeConnectionTableField> {
    const fields: Array<OtherOfficeConnectionTableField> = [];
    fields.push({ key: "officeName", label: "事業所名", sortable: false, sortDirection: "desc", class: "office-name" });
    fields.push({ key: "staffName", label: "担当者名", sortable: false, sortDirection: "desc", class: "staff-name" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["other-office-message-connection-list-tab-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  public onDetail(item: OtherOfficeConnectionTableItem): void {
    this.$emit("onDetail", item);
    return;
  }
}
</script>

<style lang="scss" scoped>
.other-office-connection-list-tab {
  width: 100%;
}
</style>
